import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Database, KeyRound, Layers, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, LoadingRows, PageHeader } from "@/components/shared";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Databases() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => api.get("/databases").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/databases", { name: name.trim().toLowerCase() });
      toast.success(`Banco "${name}" criado`);
      setOpen(false);
      setName("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/databases/${toDelete}`);
      toast.success(`Banco "${toDelete}" excluído`);
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="databases-page">
      <PageHeader overline="Gerenciamento" title="Bancos de dados">
        <Button onClick={() => setOpen(true)} data-testid="create-db-button" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">
          <Plus className="h-4 w-4 mr-1" /> Novo banco
        </Button>
      </PageHeader>

      {!items ? (
        <LoadingRows />
      ) : items.length === 0 ? (
        <EmptyState icon={Database} title="Nenhum banco criado" description="Cada banco tem armazenamento KV, coleções vetoriais e chaves de API independentes." testId="databases-empty"
          action={<Button onClick={() => setOpen(true)} data-testid="databases-empty-create" variant="outline" className="rounded-sm border-zinc-700">Criar o primeiro</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d, i) => (
            <div key={d.name} className="panel p-5 flex flex-col gap-4 row-enter hover:-translate-y-[1px] transition-transform" style={{ animationDelay: `${i * 40}ms` }} data-testid={`db-card-${d.name}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 grid place-items-center bg-zinc-800 rounded-sm shrink-0"><Database className="h-4 w-4 text-orange-500" /></div>
                  <div className="min-w-0">
                    <div className="font-mono font-medium truncate">{d.name}</div>
                    <div className="text-xs text-zinc-500">{new Date(d.created_at).toLocaleString("pt-BR")}</div>
                  </div>
                </div>
                <button onClick={() => setToDelete(d.name)} data-testid={`db-delete-${d.name}`} className="p-2 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-sm btn-tactile" title="Excluir">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-3 border border-zinc-800 divide-x divide-zinc-800 text-center">
                <div className="py-2"><div className="font-display font-bold">{fmtNum(d.keys_approx)}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">chaves</div></div>
                <div className="py-2"><div className="font-display font-bold">{fmtNum(d.vectors)}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">vetores</div></div>
                <div className="py-2"><div className="font-display font-bold">{fmtBytes(d.disk_bytes)}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">disco</div></div>
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-400">
                <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {d.collections} coleções</span>
                <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> {d.api_keys} chaves API</span>
              </div>
              <Button asChild variant="outline" className="rounded-sm border-zinc-700 hover:bg-zinc-800 btn-tactile">
                <Link to={`/databases/${d.name}`} data-testid={`db-open-${d.name}`}>Gerenciar <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="create-db-dialog">
          <form onSubmit={create}>
            <DialogHeader>
              <DialogTitle className="font-display">Novo banco</DialogTitle>
              <DialogDescription>Use letras minúsculas, números, "_" ou "-". Ele recebe uma API própria imediatamente.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="ex.: loja, crm, embeddings" pattern="[a-z0-9_\-]{1,40}" required data-testid="create-db-name-input" className="bg-transparent border-zinc-800 rounded-sm font-mono" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="create-db-cancel" className="rounded-sm">Cancelar</Button>
              <Button type="submit" disabled={busy} data-testid="create-db-submit" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title={`Excluir banco "${toDelete}"?`}
        description="Todos os dados KV, coleções vetoriais e chaves de API deste banco serão apagados do disco. Esta ação não pode ser desfeita." onConfirm={remove} testId="confirm-delete-db" />
    </div>
  );
}
